<script module>
  import img1 from './point-and-level-system-1.webp'
  import img2 from './point-and-level-system-2.webp'
  import img3 from './point-and-level-system-3.webp'
  import img4 from './point-and-level-system-4.webp'

  export const meta = {
    title: 'Point & level system',
    teaser:
      'Learn how to accumulate points and level up by attending sessions, with a bonus\nfor the first sessions of the day. Points are enhanced by multipliers that\nincrease with consecutive daily sessions.',
    sort: 5,
  }
</script>

<p>
  Every time you complete a session, you have the opportunity to rack up points.
  Here's how the system operates:
</p>
<ul>
  <li>Earn 30 points for your first session of the day.</li>
  <li>The second session grants you 20 points.</li>
  <li>Every session thereafter awards 10 points.</li>
</ul>
<p>For every 1000 points, you level up.</p>
<h2>Level Tiers</h2>
<p>Based on your level, you are placed in a tier.</p>
<table>
  <thead>
    <tr>
      <th align="left">Level</th>
      <th align="center">Tier</th>
      <th align="center">Example</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td align="left"><strong>1 - 9</strong></td>
      <td align="center">Bronze</td>
      <td align="center">Level 1 <img src={img1} alt="Bronze 1" /></td>
    </tr>
    <tr>
      <td align="left"><strong>10 - 99</strong></td>
      <td align="center">Silver</td>
      <td align="center"
        >Level 70 - 79 <img src={img2} alt="Silver tier badge" /></td
      >
    </tr>
    <tr>
      <td align="left"><strong>100 - 999</strong></td>
      <td align="center">Gold</td>
      <td align="center"
        >Level 400 - 499 <img src={img3} alt="Gold tier badge" /></td
      >
    </tr>
    <tr>
      <td align="left"><strong>1000 - 9999</strong></td>
      <td align="center">Diamond</td>
      <td align="center"
        >Level 2000 - 2999 <img src={img4} alt="Diamond tier badge" /></td
      >
    </tr>
  </tbody>
</table>
<h3>Multiplier Effect</h3>
<p>
  With each session, your points gain a little extra oomph thanks to a
  multiplier. This multiplier increases by 1 each new day, up to a cap of x10.
  Miss a day? Your multiplier decreases by 0.2, but it will always stay at x1.0
  at the minimum.
</p>
<p>
  This multiplier applies to the points you earn from each session, giving your
  total a nice boost.
</p>
<h2>Breaking it Down: A Walkthrough</h2>
<p>
  If you’re really into numbers 🤓 here's a look at an example scenario for a
  newcomer:
</p>
<table>
  <thead>
    <tr>
      <th align="center">Day</th>
      <th align="right">Total Points</th>
      <th>Mult.</th>
      <th align="center">Sessions</th>
      <th align="right">Points gained</th>
      <th align="right">Calculation</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td align="center"><strong>1</strong></td>
      <td align="right"><code>0 pts</code></td>
      <td><code>x1.0</code></td>
      <td align="center">2</td>
      <td align="right"><code>50pts</code></td>
      <td align="right"><code>(30+20)*1.0</code></td>
    </tr>
    <tr>
      <td align="center"><strong>2</strong></td>
      <td align="right"><code>50 pts</code></td>
      <td><code>x2.0</code></td>
      <td align="center">3</td>
      <td align="right"><code>120pts</code></td>
      <td align="right"><code>(30+20+10)*2.0</code></td>
    </tr>
    <tr>
      <td align="center"><strong>3</strong></td>
      <td align="right"><code>170 pts</code></td>
      <td><code>x3.0</code></td>
      <td align="center">2</td>
      <td align="right"><code>150pts</code></td>
      <td align="right"><code>(30+20)*3.0</code></td>
    </tr>
    <tr>
      <td align="center"><strong>4</strong></td>
      <td align="right"><code>320 pts</code></td>
      <td><code>x4.0</code></td>
      <td align="center">1</td>
      <td align="right"><code>120pts</code></td>
      <td align="right"><code>(30)*4.0</code></td>
    </tr>
    <tr>
      <td align="center"><strong>5</strong></td>
      <td align="right"><code>440 pts</code></td>
      <td><code>x5.0</code></td>
      <td align="center">4</td>
      <td align="right"><code>350pts</code></td>
      <td align="right"><code>(30+20+10+10)*5.0</code></td>
    </tr>
    <tr>
      <td align="center"><strong>6</strong></td>
      <td align="right"><code>790 pts</code></td>
      <td><code>x6.0</code></td>
      <td align="center">0</td>
      <td align="right"></td>
      <td align="right"></td>
    </tr>
    <tr>
      <td align="center"><strong>7</strong></td>
      <td align="right"><code>790 pts</code></td>
      <td><code>x5.8</code></td>
      <td align="center">2</td>
      <td align="right"><code>290pts</code></td>
      <td align="right"><code>(30+20)*5.8</code></td>
    </tr>
    <tr>
      <td align="center"><strong>8</strong></td>
      <td align="right"><code>1080 pts</code></td>
      <td><code>x6.8</code></td>
      <td align="center">1</td>
      <td align="right"><code>204pts</code></td>
      <td align="right"><code>(30)*6.8</code></td>
    </tr>
    <tr>
      <td align="center"><strong>9</strong></td>
      <td align="right"><code>1284 pts</code></td>
      <td><code>x7.8</code></td>
      <td align="center">0</td>
      <td align="right"></td>
      <td align="right"></td>
    </tr>
    <tr>
      <td align="center"><strong>10</strong></td>
      <td align="right"><code>1284 pts</code></td>
      <td><code>x7.6</code></td>
      <td align="center">0</td>
      <td align="right"></td>
      <td align="right"></td>
    </tr>
    <tr>
      <td align="center"><strong>11</strong></td>
      <td align="right"><code>1284 pts</code></td>
      <td><code>x7.4</code></td>
      <td align="center">0</td>
      <td align="right"></td>
      <td align="right"></td>
    </tr>
    <tr>
      <td align="center"><strong>12</strong></td>
      <td align="right"><code>1284 pts</code></td>
      <td><code>x7.2</code></td>
      <td align="center">3</td>
      <td align="right"><code>432pts</code></td>
      <td align="right"><code>(30+20+10)*7.2</code></td>
    </tr>
    <tr>
      <td align="center"><strong>13</strong></td>
      <td align="right"><code>1716 pts</code></td>
      <td><code>x8.2</code></td>
      <td align="center">etc...</td>
      <td align="right"></td>
      <td align="right"></td>
    </tr>
  </tbody>
</table>
